import {
  skipToken,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { useCallback } from 'react';
import type {
  Session,
  WorkflowDetail,
  WorkflowRun,
} from '../components/workflows/workflows.types.js';
import { useTRPC } from './trpc.js';

export const ACTIVITY_POLL_MS = 10_000;

interface PagedQuery {
  isPending: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  refetch: () => Promise<unknown>;
}

export interface CreativeMedia {
  url: string | null;
  previewUrl: string | null;
}

export interface RunResults {
  runs: (WorkflowRun | null)[];
  error: string | null;
}

function pagedList(query: PagedQuery) {
  return {
    isPending: query.isPending,
    isError: query.isError,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    retry: () => void query.refetch(),
  };
}

export function useWorkflowCatalog(accountIds: number[] = [], enabled = true) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.workflows.catalogList.queryOptions({ accountIds }), enabled });
}

// `hasSelection` drops the controls a pick decides — the creative count above
// all. False on a cold start, and on a chain carrying no pick, where the count
// is still the run's own to set.
export function useWorkflowDetail(workflowId: string | null, hasSelection = false) {
  const trpc = useTRPC();
  return useQuery(
    trpc.workflows.catalogGet.queryOptions(
      workflowId === null ? skipToken : { workflowId, hasSelection },
    ),
  );
}

// A chain's steps after the first each run on a pick, as `useWorkflowDetail` renders them.
export function useWorkflowDetails(workflowIds: string[]): WorkflowDetail[] {
  const trpc = useTRPC();
  return useQueries({
    queries: workflowIds.map((workflowId, index) =>
      trpc.workflows.catalogGet.queryOptions({ workflowId, hasSelection: index > 0 }),
    ),
    combine: (results) =>
      results.flatMap((result) => (result.data === undefined ? [] : [result.data])),
  });
}

export function usePlatforms(enabled: boolean) {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.workflows.platforms.queryOptions(),
    enabled,
  });
  return query.data?.platforms ?? [];
}

export function usePlatformsById(): Record<number, { slug: string; label: string }> {
  const trpc = useTRPC();
  const query = useQuery(trpc.workflows.platforms.queryOptions());
  return Object.fromEntries(
    (query.data?.platforms ?? []).map((platform) => [
      platform.networkId,
      { slug: platform.slug, label: platform.label },
    ]),
  );
}

export function useCreativeThumbnails(runId: string | null): Record<string, CreativeMedia> {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.workflows.creativeThumbnails.queryOptions({ runId: runId ?? '' }),
    enabled: runId !== null,
  });
  return Object.fromEntries(
    (query.data?.thumbnails ?? []).map((media) => [
      media.creativeId,
      { url: media.url, previewUrl: media.previewUrl },
    ]),
  );
}

export function useBenchmarkCount(
  dateRange: [string, string],
  filters: Record<string, string[]>,
  selected: Record<string, string>,
  enabled: boolean,
) {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.workflows.benchmarkCount.queryOptions({ dateRange, filters, selected }),
    enabled,
  });
  return {
    count: query.data?.count ?? 0,
    minimum: query.data?.minimum ?? 0,
    isEnough: query.data?.isEnough ?? true,
    isFetching: query.isPending || query.isFetching,
  };
}

export function usePlatformFields(platform: string | null, selected: Record<string, string>) {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.workflows.platformFields.queryOptions({ platform: platform ?? '', selected }),
    enabled: platform !== null,
    placeholderData: (previous) => (previous?.platform === platform ? previous : undefined),
  });
  return { fields: query.data?.fields ?? [], isFetching: query.isFetching, isError: query.isError };
}

export function usePrefetchPlatformFields(): (platform: string) => void {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return (platform: string) => {
    void queryClient.prefetchQuery(
      trpc.workflows.platformFields.queryOptions({ platform, selected: {} }),
    );
  };
}

export const NO_STREAM = { context: { stream: false } };
const POLL_MS = 2_000;
// Tolerate a few slow or dropped polls before calling the run unreadable.
const POLL_RETRIES = 3;

// No status means the run has not answered yet, or the last tick failed and
// left the previous answer in place. Either way it is still worth asking.
export function pollAgain(status: string | undefined): number | false {
  return status === 'completed' || status === 'failed' ? false : POLL_MS;
}

function retryPoll(failureCount: number, error: unknown): boolean {
  return !isForbidden(error) && failureCount < POLL_RETRIES;
}

export function isForbidden(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const data = error.data as { code?: string } | undefined;
  return data?.code === 'FORBIDDEN';
}

export function useStartRun() {
  const trpc = useTRPC();
  return useMutation(trpc.workflows.startRun.mutationOptions({ trpc: NO_STREAM }));
}

// The session is re-read on the mutation itself, not per mutate() call: those callbacks are
// dropped once the caller unmounts, which would leave a cached session without the new run.
export function useChainNext() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.workflows.chainNext.mutationOptions({
      trpc: NO_STREAM,
      onSettled: () => void queryClient.invalidateQueries(trpc.workflows.getSession.pathFilter()),
    }),
  );
}

/** Whether a chain request from `runId` into `actionType` is still out, from any component. */
export function useChainInFlight(runId: string | null, actionType: string): boolean {
  const trpc = useTRPC();
  const inFlight = useIsMutating({
    mutationKey: trpc.workflows.chainNext.mutationKey(),
    predicate: (mutation) => chainsFrom(mutation.state.variables, runId, actionType),
  });
  return inFlight > 0;
}

function chainsFrom(variables: unknown, runId: string | null, actionType: string): boolean {
  if (typeof variables !== 'object' || variables === null) return false;
  return (
    'runId' in variables &&
    variables.runId === runId &&
    'actionType' in variables &&
    variables.actionType === actionType
  );
}

export function useOutcomes(
  limit: number,
  refetchInterval: number | false = false,
  accountIds: number[] = [],
) {
  const trpc = useTRPC();
  const query = useInfiniteQuery(
    trpc.workflows.outcomes.infiniteQueryOptions(
      { limit, accountIds },
      { getNextPageParam: (page) => page.nextCursor, refetchInterval },
    ),
  );
  return {
    outcomes: (query.data?.pages ?? []).flatMap((page) => page.outcomes),
    pagesLoaded: query.data?.pages.length ?? 0,
    ...pagedList(query),
  };
}

export function useSessionList(limit: number, accountIds: number[] = []) {
  const trpc = useTRPC();
  return useQuery(
    trpc.workflows.sessionList.queryOptions(
      { limit, accountIds },
      {
        refetchInterval: (query) =>
          query.state.data?.sessions.some((session) => session.status === 'running')
            ? ACTIVITY_POLL_MS
            : false,
      },
    ),
  );
}

export function useWorkflowRuns(limit: number) {
  const trpc = useTRPC();
  const query = useInfiniteQuery(
    trpc.workflows.runs.infiniteQueryOptions(
      { limit },
      {
        getNextPageParam: (page) => page.nextCursor,
        // A run started elsewhere since the last visit must show, and arm the poll.
        refetchOnMount: 'always',
        refetchInterval: (query) =>
          query.state.data?.pages.some((page) => page.runs.some((run) => run.status === 'running'))
            ? ACTIVITY_POLL_MS
            : false,
      },
    ),
  );
  return {
    runs: (query.data?.pages ?? []).flatMap((page) => page.runs),
    total: query.data?.pages[0]?.total ?? 0,
    ...pagedList(query),
  };
}

export function useRerun() {
  const trpc = useTRPC();
  return useMutation(trpc.workflows.rerun.mutationOptions({ trpc: NO_STREAM }));
}

// `unattended`: nobody is watching, so keep polling in a hidden tab and ride out a few
// dropped ticks the way a run result poll does.
export function useSession(
  runId: string | null,
  keepPolling: (session: Session | null | undefined) => boolean = () => false,
  unattended = false,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.workflows.getSession.queryOptions(runId === null ? skipToken : { runId }, {
      refetchInterval: (query) => (keepPolling(query.state.data) ? POLL_MS : false),
      ...(unattended && {
        refetchIntervalInBackground: true,
        retry: retryPoll,
        retryDelay: POLL_MS,
      }),
      trpc: NO_STREAM,
    }),
  );
}

// Every cached session, whichever run of it a query is keyed by.
export function useRefreshSessions() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries(trpc.workflows.getSession.pathFilter()),
    [queryClient, trpc],
  );
}

export function useCreativeRuns(runId: string | null, awaiting: string | null = null) {
  const trpc = useTRPC();
  const { data } = useQuery(
    trpc.workflows.creativeRuns.queryOptions(runId === null ? skipToken : { runId }, {
      refetchInterval: (query) => {
        const listed = query.state.data?.runs.some((run) => run.runId === awaiting) ?? false;
        return awaiting === null || listed ? false : POLL_MS;
      },
      trpc: NO_STREAM,
    }),
  );
  return { runs: data?.runs ?? [], folds: data?.folds ?? [] };
}

export function useRunResult(runId: string | null) {
  const trpc = useTRPC();
  return useQuery(
    trpc.workflows.getRunResult.queryOptions(runId === null ? skipToken : { runId }, {
      staleTime: 0,
      // A slow tick is not a failed run: the run keeps going without us. Without
      // this, one 502 drops the step out of its running state and shows an error.
      retry: retryPoll,
      retryDelay: POLL_MS,
      refetchInterval: (query) =>
        isForbidden(query.state.error) ? false : pollAgain(query.state.data?.status),
      trpc: NO_STREAM,
    }),
  );
}

// Polls like useRunResult, also in a hidden tab: an unattended run only advances on these.
export function useRunResults(runIds: string[]): RunResults {
  const trpc = useTRPC();
  return useQueries({
    queries: runIds.map((runId) =>
      trpc.workflows.getRunResult.queryOptions(
        { runId },
        {
          staleTime: 0,
          retry: retryPoll,
          retryDelay: POLL_MS,
          refetchInterval: (query) =>
            isForbidden(query.state.error) ? false : pollAgain(query.state.data?.status),
          refetchIntervalInBackground: true,
          trpc: NO_STREAM,
        },
      ),
    ),
    combine: (results) => ({
      runs: results.map((result) => result.data ?? null),
      error: results.find((result) => result.error !== null)?.error?.message ?? null,
    }),
  });
}

export function useRescoreCreative() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation({
    ...trpc.workflows.rescoreCreative.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useShareRun() {
  const trpc = useTRPC();
  return useMutation(trpc.workflows.shareRun.mutationOptions({ trpc: NO_STREAM }));
}
